import { ThemeEvents, CartUpdateEvent, CartErrorEvent } from '@theme/events';
import { cartItemsSectionIds } from '@theme/utilities';

const SOURCE_ID = 'cart-upsell';
const ADD_SELECTOR = '[data-cart-upsell-add]';
const PREV_SELECTOR = '[data-cart-upsell-prev]';
const NEXT_SELECTOR = '[data-cart-upsell-next]';
const SLIDES_SELECTOR = '[data-cart-upsell-slides]';

/**
 * Adds a product to the cart via /cart/add.js without closing the drawer.
 * Dispatches CartUpdateEvent so cart-items-component morphs the cart in place.
 *
 * @param {HTMLButtonElement} button
 */
async function handleAddClick(button) {
  if (button.disabled || button.getAttribute('aria-busy') === 'true') return;

  const variantId = Number(button.dataset.variantId);
  if (!variantId) return;

  button.setAttribute('aria-busy', 'true');

  try {
    // Ask for the cart sections up front: listeners morph this markup in place.
    // Without it they fall back to a full section re-render, which swaps out the
    // live cart DOM and leaves the drawer's morph target stale.
    const sections = cartItemsSectionIds();

    const addResponse = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        sections,
        sections_url: window.location.pathname,
      }),
    });

    const addPayload = await addResponse.json();
    if (!addResponse.ok || addPayload.status || addPayload.errors) {
      throw new Error(addPayload.description || addPayload.message || 'Erro ao adicionar produto.');
    }

    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();

    document.dispatchEvent(
      new CartUpdateEvent(cart, SOURCE_ID, {
        source: SOURCE_ID,
        itemCount: cart?.item_count ?? 0,
        productId: addPayload.product_id ?? null,
        sections: addPayload.sections,
      })
    );
  } catch (error) {
    document.dispatchEvent(
      new CartErrorEvent(SOURCE_ID, error?.message || 'Erro ao adicionar produto.', 'Erro no upsell', {})
    );
  } finally {
    button.removeAttribute('aria-busy');
  }
}

/**
 * Compute scroll delta to advance one slide.
 * @param {HTMLElement} slides
 * @param {1|-1} direction
 */
function scrollSlides(slides, direction) {
  const firstSlide = slides.querySelector('.cart-upsell__slide');
  if (!(firstSlide instanceof HTMLElement)) return;
  const gap = parseInt(getComputedStyle(slides).columnGap || '0', 10) || 0;
  const delta = (firstSlide.offsetWidth + gap) * direction;
  slides.scrollBy({ left: delta, behavior: 'smooth' });
}

/**
 * Update arrow disabled state based on scroll position.
 * @param {HTMLElement} container - the .cart-upsell__carousel
 */
function syncArrows(container) {
  const slides = container.querySelector(SLIDES_SELECTOR);
  const prev = container.querySelector(PREV_SELECTOR);
  const next = container.querySelector(NEXT_SELECTOR);
  if (!(slides instanceof HTMLElement)) return;

  const max = slides.scrollWidth - slides.clientWidth;
  const atStart = slides.scrollLeft <= 1;
  const atEnd = slides.scrollLeft >= max - 1;
  const fits = max <= 1;

  if (prev instanceof HTMLButtonElement) {
    prev.disabled = atStart || fits;
    prev.toggleAttribute('hidden', fits);
  }
  if (next instanceof HTMLButtonElement) {
    next.disabled = atEnd || fits;
    next.toggleAttribute('hidden', fits);
  }
}

function initContainer(container) {
  if (container.dataset.cartUpsellInit === '1') return;
  container.dataset.cartUpsellInit = '1';

  const slides = container.querySelector(SLIDES_SELECTOR);
  if (!(slides instanceof HTMLElement)) return;

  // Prev/next clicks handled by event delegation below — no direct listeners here.
  slides.addEventListener('scroll', () => syncArrows(container), { passive: true });

  syncArrows(container);

  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(() => syncArrows(container));
    ro.observe(slides);
  }
}

function initAll() {
  document.querySelectorAll('.cart-upsell__carousel').forEach((el) => {
    if (el instanceof HTMLElement) initContainer(el);
  });
}

function syncAllCarousels() {
  document.querySelectorAll('.cart-upsell__carousel').forEach((el) => {
    if (el instanceof HTMLElement) syncArrows(el);
  });
}

// Unified click delegation: add-to-cart, prev arrow, next arrow.
// Using delegation avoids stale references after section morphs replace DOM nodes.
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const prevBtn = target.closest(PREV_SELECTOR);
  if (prevBtn instanceof HTMLButtonElement) {
    const carousel = prevBtn.closest('.cart-upsell__carousel');
    const slides = carousel instanceof HTMLElement ? carousel.querySelector(SLIDES_SELECTOR) : null;
    if (slides instanceof HTMLElement) scrollSlides(slides, -1);
    return;
  }

  const nextBtn = target.closest(NEXT_SELECTOR);
  if (nextBtn instanceof HTMLButtonElement) {
    const carousel = nextBtn.closest('.cart-upsell__carousel');
    const slides = carousel instanceof HTMLElement ? carousel.querySelector(SLIDES_SELECTOR) : null;
    if (slides instanceof HTMLElement) scrollSlides(slides, 1);
    return;
  }

  const addBtn = target.closest(ADD_SELECTOR);
  if (addBtn instanceof HTMLButtonElement) {
    event.preventDefault();
    handleAddClick(addBtn);
  }
});

// Re-init after cart morph so scroll listeners + ResizeObservers stay attached
// to the current DOM nodes.
document.addEventListener(ThemeEvents.cartUpdate, () => {
  initAll();
});

// showDialog() in dialog.js defers showModal() to a requestAnimationFrame, so
// all synchronous cartUpdate listeners fire while the dialog is still closed
// (scrollWidth / clientWidth = 0). Re-sync arrow states once the dialog is
// actually open and layout is available.
document.addEventListener('dialog:open', syncAllCarousels);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
